import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import ReactGA from 'react-ga4';
import logo from '../assets/images/finaculator1.png';

interface InterestPDFGeneratorProps {
  interestType: "simple" | "compound";
  principal: number;
  rate: number;
  period: number | null;
  periodUnit: "years" | "months";
  totalInterest: number;
  totalAmount: number;
  compoundInterval?: string;
  formatNumberWithCommas: (value: number | string) => string;
  startDate: string; 
  endDate: string;   
}

const InterestPDFGenerator: React.FC<InterestPDFGeneratorProps> = ({
  interestType,
  principal,
  rate,
  period,
  periodUnit,
  totalInterest,
  totalAmount,
  compoundInterval,
  formatNumberWithCommas,
  startDate, 
  endDate,   
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const generatePDF = () => {
    if (!principal || !rate || period === null || totalInterest === 0 || totalAmount === 0) {
      setSnackbarMessage("Please ensure all values are calculated and not empty before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }

    ReactGA.event({
      category: 'Interest Calculation PDF',
      action: 'Download Interest Calculation PDF Button',
      label: 'Interest Calculation PDF'
    });

    const doc = new jsPDF();
    const title = 'Interest Summary';
    const effectiveRate = rate || 0;

    const detailsData = [
      { label: 'Principal Amount', value: formatNumberWithCommas(principal.toString()) },
      { label: 'Interest Rate', value: `${effectiveRate}%` },
      ...(interestType === "compound" ? [
        { label: 'Compound Interval', value: compoundInterval || 'N/A' },
        { label: 'Start Date', value: startDate || 'N/A' }, 
        { label: 'End Date', value: endDate || 'N/A' },     
      ] : [
        { label: 'Period', value: `${period} ${periodUnit}` },
      ]),
    ];

    doc.setFontSize(14);
    doc.text(title, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });

    autoTable(doc, {
      head: [['Field', 'Value']],
      body: detailsData.map(row => [row.label, row.value]),
      startY: 30,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
      columnStyles: {
        0: { cellWidth: 50 }, 
        1: { cellWidth: 120, overflow: 'linebreak' }, 
      },
      didParseCell: (data) => {
        if (data.column.index === 1) {
          data.cell.styles.overflow = 'linebreak'; 
          data.cell.styles.cellWidth = 'auto'; 
        }
      },
    });

    const roundedTotalInterest = Math.ceil(totalInterest);
    const roundedTotalAmount = Math.ceil(totalAmount);

    if (interestType === "simple") {
      autoTable(doc, {
        head: [['Detail', 'Value']],
        body: [
          ['Total Interest', formatNumberWithCommas(roundedTotalInterest)],
          ['Maturity Amount', formatNumberWithCommas(principal + roundedTotalInterest)],
        ],
        startY: doc.autoTable.previous.finalY + 10,
        theme: 'grid',
        styles: { halign: 'center' },
      });
    } else if (interestType === "compound") {
      autoTable(doc, {
        head: [['Detail', 'Value']],
        body: [
          ['Total Interest', formatNumberWithCommas(roundedTotalInterest)],
          ['Final Amount', formatNumberWithCommas(roundedTotalAmount)],
        ],
        startY: doc.autoTable.previous.finalY + 10,
        theme: 'grid',
        styles: { halign: 'center' },
      });
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.src = logo;
    img.onload = function () {
      const watermarkWidth = 100;
      const watermarkHeight = 100;

      canvas.width = watermarkWidth;
      canvas.height = watermarkHeight;

      if (ctx) {
        ctx.globalAlpha = 0.1;
        ctx.drawImage(img, 0, 0, watermarkWidth, watermarkHeight);

        const imgData = canvas.toDataURL('image/png');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const xPosition = (pageWidth - watermarkWidth) / 2;
        const yPosition = (pageHeight - watermarkHeight) / 2;

        doc.addImage(imgData, 'PNG', xPosition, yPosition, watermarkWidth, watermarkHeight);
        const now = new Date();
        const padZero = (num: string | number) => ("0" + num).slice(-2);
        const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
        const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
        const timestamp = `${formattedDate}_${formattedTime}`;
        const fileName = `finaculator_interest_calculator_${timestamp}.pdf`;
        doc.save(fileName);
      }
    };
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70, marginRight: 90 }}
        startIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>

      {/* Snackbar for validation alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default InterestPDFGenerator;
